/**
 * Example Individual Dashboard payload, shaped exactly like the API's
 * `data` (cards.sections S_2 … S_5). Used by the program-creation preview's
 * "View Impact" tab (via DashboardMockProvider) so a program creator sees the
 * real dashboard pages populated with believable figures — every section has
 * content, none of it suppressed.
 *
 * Readiness bands follow the live rubric: High ≥ 80, Moderate 60–79, Basic 40–59, Low < 40.
 */

const classAvg = (value) => [{name: 'Class avg.', value}];

const READINESS_AREAS = [
    {code: 'RA_1', sublabel: 'Readiness Area 1', label: 'Youth AI-Digital Activities',            value: 83, band: 'High',     cls: 66},
    {code: 'RA_2', sublabel: 'Readiness Area 2', label: 'Self-Regulated AI-Digital Engagement',   value: 50, band: 'Basic',    cls: 54},
    {code: 'RA_3', sublabel: 'Readiness Area 3', label: 'Digital Hygiene and Privacy Management', value: 75, band: 'Moderate', cls: 61},
    {code: 'RA_4', sublabel: 'Readiness Area 4', label: 'Cyberbullying Management',               value: 25, band: 'Low',      cls: 48},
    {code: 'RA_5', sublabel: 'Readiness Area 5', label: 'Content Risk Avoidance',                 value: 92, band: 'High',     cls: 70},
    {code: 'RA_6', sublabel: 'Readiness Area 6', label: 'Contact Risk Avoidance',                 value: 67, band: 'Moderate', cls: 59},
    {code: 'RA_7', sublabel: 'Readiness Area 7', label: 'Authentic and Ethical Digital Identity', value: 58, band: 'Basic',    cls: 52},
    {code: 'RA_8', sublabel: 'Readiness Area 8', label: 'Critical Information Management',        value: 67, band: 'Moderate', cls: 55},
].map(({cls, ...a}) => ({...a, benchmark_values: classAvg(cls)}));

const CONTEXT_AREAS = [
    {code: 'CS_1', sublabel: 'Context & Support Area 1', label: 'Digital Access at Home and School',               value: 72, band: 'Adequate', cls: 68},
    {code: 'CS_2', sublabel: 'Context & Support Area 2', label: 'Youth Perception of Home and Support Environment', value: 88, band: 'Strong',   cls: 74},
].map(({cls, ...a}) => ({...a, benchmark_values: classAvg(cls)}));

const byValueDesc = (a, b) => b.value - a.value;

// Learning Progress rows: pre → post on the program's target area; impact rate = gain / headroom.
const PROGRAMS = [
    {name: 'Upper Primary Cyberbullying Awareness', target: 'Cyberbullying Management',               status: 'Completed', pass: 'Pass',     pre: 25, post: 58, signal: 'Strong Signal'},
    {name: 'Generative AI for Lower Primary',       target: 'Self-Regulated AI-Digital Engagement',   status: 'Completed', pass: 'Pass',     pre: 38, post: 63, signal: 'Moderate Signal'},
    {name: 'High School AI Agent Development',      target: 'Authentic and Ethical Digital Identity', status: 'Active',    pass: 'Not Pass', pre: 50, post: 58, signal: 'Early'},
];
const impactRate = (p) => Math.round(((p.post - p.pre) / (100 - p.pre)) * 100);

/**
 * The sample payload. `programName` (optional) puts the program being
 * created at the top of "My programs", so the preview reads as its own result.
 */
export const buildSampleIndividualDashboard = ({programName} = {}) => {
    const programs = programName
        ? [{...PROGRAMS[0], name: programName}, ...PROGRAMS.slice(1)]
        : PROGRAMS;
    const composite = Math.trunc(READINESS_AREAS.reduce((s, a) => s + a.value, 0) / READINESS_AREAS.length);
    const classComposite = 58;
    const strengths = READINESS_AREAS.filter((a) => a.band === 'High');
    const grow = READINESS_AREAS.filter((a) => a.band === 'Low');
    const passed = programs.filter((p) => p.pass === 'Pass').length;
    const avgImpact = Math.round(programs.reduce((s, p) => s + impactRate(p), 0) / programs.length);
    const col = (column_name, column_type, values) => ({column_name, column_type, values});

    return {
        cards: {
            sections: [
                {
                    section_code: 'S_2',
                    cards: [
                        {card_code: 'C_1', card_type: 'TitleCard', title: 'My Score Overview', footer: 'What does my overall profile show?'},
                        {
                            card_code: 'C_2', card_type: 'ORGCard',
                            table_objects: [
                                col('Student', 'text', ['Alex Tan']),
                                col('Class', 'text', ['Class 5B']),
                                col('Grade', 'text', ['Grade 5']),
                                col('School', 'text', ['Sample Primary School']),
                                col('Assessment Window', 'text', ['2026-09-01 ~ 2026-09-12']),
                                col('Report Date', 'text', ['2026-10-13']),
                            ],
                        },
                        {card_code: 'C_3',  card_type: 'KPICard', kpi_name: 'My Readiness Composite Score', kpi_value: composite, kpi_band: 'Moderate'},
                        {card_code: 'C_3a', card_type: 'KPICard', kpi_name: 'Strengths', kpi_value: strengths.length},
                        {card_code: 'C_3b', card_type: 'KPICard', kpi_name: 'Weaknesses', kpi_value: grow.length},
                        {card_code: 'C_4',  card_type: 'KPICard', kpi_name: 'My Class Average', kpi_value: classComposite, kpi_band: 'Basic'},
                        {card_code: 'C_5',  card_type: 'BarChartBMCard', title: 'Top Readiness Area Scores', items: [...READINESS_AREAS].sort(byValueDesc).slice(0, 3)},
                        {card_code: 'C_5a', card_type: 'BarChartBMCard', title: 'Top Context & Support Scores', items: [...CONTEXT_AREAS].sort(byValueDesc)},
                    ],
                },
                {
                    section_code: 'S_3',
                    cards: [
                        {card_code: 'C_1',  card_type: 'TitleCard', title: 'My Online Safety Readiness', footer: 'What are my strengths and growth areas?'},
                        {card_code: 'C_2',  card_type: 'BarChartBMCard', title: 'Readiness Area Scores', items: READINESS_AREAS},
                        {card_code: 'C_2a', card_type: 'ListCard', items: strengths},
                        {card_code: 'C_2b', card_type: 'ListCard', items: grow},
                    ],
                },
                {
                    section_code: 'S_4',
                    cards: [
                        {card_code: 'C_1', card_type: 'TitleCard', title: 'My Context & Support', footer: 'What support conditions do I have?'},
                        {card_code: 'C_2', card_type: 'BarChartBMCard', title: 'Context & Support Area Scores', items: CONTEXT_AREAS},
                    ],
                },
                {
                    section_code: 'S_5',
                    cards: [
                        {card_code: 'C_2', card_type: 'KPICard', kpi_name: 'Active Programs', kpi_value: programs.length},
                        {card_code: 'C_3', card_type: 'KPICard', kpi_name: 'Passed Programs', kpi_value: `${passed} of ${programs.length}`},
                        {card_code: 'C_4', card_type: 'KPICard', kpi_name: 'Avg Impact Rate', kpi_value: avgImpact},
                        {
                            card_code: 'C_5', card_type: 'TableCard', title: 'My programs',
                            table_objects: [
                                col('Program', 'text', programs.map((p) => p.name)),
                                col('Target Area', 'text', programs.map((p) => p.target)),
                                col('Status', 'status', programs.map((p) => p.status)),
                                col('Pass / Not Pass', 'badge', programs.map((p) => p.pass)),
                                col('Pre → Post', 'text', programs.map((p) => `${p.pre} → ${p.post}`)),
                                col('Learning Impact Rate %', 'percent', programs.map(impactRate)),
                                col('Learning signal', 'badge', programs.map((p) => p.signal)),
                            ],
                        },
                    ],
                },
            ],
        },
    };
};
